import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { PartyPopper, ArrowRight, Trophy, KeyRound, LayoutDashboard, Package } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

/**
 * Universal Onboarding · Step 6 — Congratulations.
 *
 * Wraps up the flow with entry-type-specific copy:
 *   • registration       → "Welcome to CollectorUp!"
 *   • certificate_claim  → "Congratulations! Your Album Complete
 *                           Certificate has been successfully claimed."
 *
 * Fires confetti EXACTLY once — only on the initial successful
 * completion (not on any subsequent revisit). Respects
 * `prefers-reduced-motion` for accessibility.
 *
 * Persists `onboarding.status='complete'` and `onboarding.completed_at`
 * via `POST /me/onboarding/complete` on mount (idempotent). Once the
 * status is `complete` the CollectorProtectedRoute gate stops
 * bouncing the user back into onboarding — they are free to use the
 * normal CollectorUp experience.
 */
export default function OnboardingCongrats() {
  const navigate = useNavigate();
  const { collector, refresh } = useCollectorAuth();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  // Snapshot the pre-mount status so we can distinguish "just finished
  // right now" from "the user is revisiting an already-complete flow".
  // Confetti fires only on the former.
  const wasAlreadyCompleteAtMount = useRef(false);
  const confettiFired = useRef(false);

  const ob = collector?.onboarding || {};
  const isClaim = ob.entry_type === "certificate_claim";
  const certNumber = ob.entry_cert_number;

  // C2 — Congrats no longer presents a "successfully claimed" screen.
  // Under C1 the certificate_claim entries route directly from the
  // album step to `/dashboard/claim?cert=…` (the ClaimCodeForm); any
  // path that still lands here with an unfinished claim would be
  // showing a false success. Bounce back to the code form so the user
  // completes the physical claim before the celebration. Kept inside
  // an effect (rather than an early `return <Navigate />`) so all the
  // hooks below still run on the initial render — React hooks rule.
  useEffect(() => {
    if (isClaim && certNumber) {
      navigate(`/dashboard/claim?cert=${encodeURIComponent(certNumber)}`, { replace: true });
    }
  }, [isClaim, certNumber, navigate]);

  // Persist completion + capture the "already complete" flag.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      wasAlreadyCompleteAtMount.current = ob.status === "complete";
      if (ob.status !== "complete") {
        try {
          await api.post("/me/onboarding/complete");
          await refresh();
        } catch (_) {
          // Non-fatal — the dashboard CTA is still available.
        }
      }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // One-time confetti — fires ONLY on the initial successful completion.
  useEffect(() => {
    if (!ready) return;
    if (confettiFired.current) return;
    if (wasAlreadyCompleteAtMount.current) {
      // The user is revisiting a finished onboarding — no confetti.
      confettiFired.current = true;
      return;
    }
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      confettiFired.current = true;
      return;
    }
    confettiFired.current = true;
    const shoot = (opts) => confetti({
      particleCount: 60,
      spread: 70,
      startVelocity: 45,
      colors: ["#F5C518", "#E30613", "#0A0A0A", "#FFFFFF"],
      ...opts,
    });
    shoot({ origin: { x: 0.2, y: 0.7 } });
    setTimeout(() => shoot({ origin: { x: 0.8, y: 0.7 } }), 160);
    setTimeout(() => shoot({ particleCount: 90, spread: 100, origin: { x: 0.5, y: 0.3 } }), 380);
  }, [ready]);

  const goDashboard = async () => {
    setBusy(true);
    try {
      // Belt-and-braces — the mount effect already called this, but
      // an inflight failure would otherwise strand the user with an
      // in_progress status. Safe under retry.
      await api.post("/me/onboarding/complete");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data) || "Almost there…");
    } finally {
      setBusy(false);
      navigate("/dashboard");
    }
  };

  // ─── Copy variants — Congrats now only handles ordinary
  //     registration completions (see the C2 redirect above for
  //     certificate_claim entries).
  const headline = "Welcome to CollectorUp!";
  const kicker = { label: "You’re All Set", icon: <PartyPopper size={14} className="text-[var(--panini-red)]" /> };

  return (
    <div data-testid="onboarding-congrats" className="text-center">
      <div
        className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-1.5 text-xs font-black uppercase tracking-widest"
        data-testid="onboarding-congrats-kicker"
      >
        {kicker.icon}
        {kicker.label}
      </div>

      <h1
        className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6 max-w-3xl mx-auto"
        data-testid="onboarding-congrats-headline"
      >
        {headline}
      </h1>

      {/* Mission statement — mandated by owner. Never mentions a
          specific edition; CollectorUp helps complete EVERY World Cup
          album a collector owns. */}
      <p
        className="mt-6 text-lg text-[var(--smoke)] max-w-2xl mx-auto"
        data-testid="onboarding-congrats-mission"
      >
        Our mission is to help you complete every FIFA World Cup album you own.
      </p>

      {/* Public Collector ID panel */}
      <div className="mt-10 rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[10px_10px_0_var(--panini-yellow)] max-w-xl mx-auto">
        <div className="mx-auto h-14 w-14 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] flex items-center justify-center">
          <Trophy size={26} className="text-[var(--panini-red)]" />
        </div>
        <div className="mt-4 kicker kicker-black text-[11px]">
          Public Collector ID
        </div>
        <div
          className="mt-1 font-mono text-2xl font-black text-[var(--panini-red)]"
          data-testid="onboarding-public-id"
        >
          {collector?.public_collector_id || "—"}
        </div>
        <button
          data-testid="onboarding-goto-dashboard"
          onClick={goDashboard}
          disabled={busy}
          className="btn-red mt-6 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? "Finishing…" : "Open my dashboard"} <ArrowRight size={18} />
        </button>
      </div>

      {/* "What's next" hints — points the collector at the normal
          CollectorUp experience they are now free to explore. */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
        <NextStepCard
          icon={<LayoutDashboard size={18} className="text-[var(--ink)]" />}
          title="Your dashboard"
          copy="A single home for your certificates, orders, and profile."
          testid="onboarding-next-dashboard"
        />
        <NextStepCard
          icon={<Package size={18} className="text-[var(--ink)]" />}
          title="Your collection"
          copy="Track every album you own and share your favorites publicly."
          testid="onboarding-next-collection"
        />
        <NextStepCard
          icon={<KeyRound size={18} className="text-[var(--ink)]" />}
          title="Your first certificate"
          copy="Received an Album Complete Certificate? Claim it any time from your dashboard."
          testid="onboarding-next-certificate"
        />
      </div>
    </div>
  );
}

function NextStepCard({ icon, title, copy, testid }) {
  return (
    <div
      className="rounded-2xl border-2 border-[var(--ink)]/15 bg-white/70 p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-center">
          {icon}
        </div>
        <div className="font-black text-[var(--ink)]">{title}</div>
      </div>
      <p className="mt-2 text-sm text-[var(--smoke)]">{copy}</p>
    </div>
  );
}
